import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useGameLoop } from '../Shared/hooks/useGameLoop';
import { useAudio } from '../Shared/hooks/useAudio';
import GameLayout from '../Shared/components/GameLayout/GameLayout';
import Scoreboard from '../Shared/components/Scoreboard/Scoreboard';
import Countdown from '../Shared/components/Countdown/Countdown';
import { GAME_THEME } from '../Shared/constants/GameTheme';
import styles from './Click6.module.css';

const Click6 = () => {
    const canvasRef = useRef(null);
    const { beep, ping } = useAudio();

    // Constant Game Settings
    const TARGET_RADIUS = 40;

    // Game State Ref
    const stateRef = useRef({
        running: false,
        startTime: 0,
        endTime: 0,
        targets: [], // [{x, y, active}]
        mode: 'random',
    });

    // UI State
    const [mode, setMode] = useState('random'); // 'random', 'grid', 'alternate'
    const [gameState, setGameState] = useState('menu'); // 'menu', 'playing', 'finished'
    const [lastTime, setLastTime] = useState(0);
    const [highScores, setHighScores] = useState({ random: [], grid: [], alternate: [], column: [], w_pattern: [], ordered_random: [] });
    const [showStartModal, setShowStartModal] = useState(true);
    const [isCountingDown, setIsCountingDown] = useState(false);

    // Helpers
    const loadScores = useCallback(() => {
        const stats = JSON.parse(localStorage.getItem("click6Scores") || "{\"random\":[], \"grid\":[], \"alternate\":[], \"column\":[], \"w_pattern\":[], \"ordered_random\":[]}");
        setHighScores(stats);
    }, []);

    const saveScore = (totalTime, mde) => {
        const stats = JSON.parse(localStorage.getItem("click6Scores") || "{\"random\":[], \"grid\":[], \"alternate\":[], \"column\":[], \"w_pattern\":[], \"ordered_random\":[]}");
        if (!stats[mde]) stats[mde] = [];
        stats[mde].push({ time: totalTime, date: new Date().toISOString() });
        stats[mde].sort((a, b) => a.time - b.time);
        stats[mde] = stats[mde].slice(0, 10);
        localStorage.setItem("click6Scores", JSON.stringify(stats));
        loadScores();
    };

    const clearScores = useCallback(() => {
        localStorage.removeItem("click6Scores");
        loadScores();
    }, [loadScores]);

    useEffect(() => {
        loadScores();
    }, [loadScores]);

    const checkOverlap = (newTarget, existingTargets) => {
        for (let t of existingTargets) {
            const dist = Math.hypot(newTarget.x - t.x, newTarget.y - t.y);
            if (dist < (TARGET_RADIUS * 2 + 10)) {
                return true;
            }
        }
        return false;
    };

    const generateTargets = (width, height, currentMode) => {
        const targets = [];
        const pad = TARGET_RADIUS + 20;

        if (currentMode === 'random') {
            for (let i = 0; i < 6; i++) {
                let attempts = 0;
                let valid = false;
                let newTarget;

                while (!valid && attempts < 100) {
                    newTarget = {
                        x: Math.random() * (width - 2 * pad) + pad,
                        y: Math.random() * (height - 2 * pad) + pad,
                        active: true
                    };
                    if (!checkOverlap(newTarget, targets)) {
                        valid = true;
                    }
                    attempts++;
                }

                if (valid) {
                    targets.push(newTarget);
                } else {
                    targets.push({
                        x: width / 2 + (i * 10),
                        y: height / 2 + (i * 10),
                        active: true
                    });
                }
            }
        } else if (currentMode === 'grid') { // 1 row, 6 columns
            const count = 6;
            const spacingX = TARGET_RADIUS * 2.5;
            const totalWidth = (count - 1) * spacingX;
            const startX = width / 2 - totalWidth / 2;
            const centerY = height / 2;

            for (let i = 0; i < count; i++) {
                targets.push({
                    x: startX + i * spacingX,
                    y: centerY,
                    active: true
                });
            }
        } else if (currentMode === 'alternate') { // left right left right...
            const leftX = width * 0.25;
            const rightX = width * 0.75;
            const centerY = height / 2;

            for (let i = 0; i < 6; i++) {
                targets.push({
                    x: i % 2 === 0 ? leftX : rightX,
                    y: centerY,
                    active: true
                });
            }
        } else if (currentMode === 'column') { // 1 column, 6 rows
            const count = 6;
            const spacingY = TARGET_RADIUS * 2.5;
            const totalHeight = (count - 1) * spacingY;
            const centerX = width / 2;
            const startY = height / 2 - totalHeight / 2;

            for (let i = 0; i < count; i++) {
                targets.push({
                    x: centerX,
                    y: startY + i * spacingY,
                    active: true
                });
            }
        } else if (currentMode === 'w_pattern') { // W shape — ordered left to right
            const topY = height * 0.25;
            const bottomY = height * 0.75;
            const midY = height * 0.45;
            const wPoints = [
                { x: width * 0.10, y: topY }, // 1: top-left
                { x: width * 0.28, y: bottomY }, // 2: bottom-left valley
                { x: width * 0.43, y: midY }, // 3: inner peak left
                { x: width * 0.57, y: midY }, // 4: inner peak right
                { x: width * 0.72, y: bottomY }, // 5: bottom-right valley
                { x: width * 0.90, y: topY }, // 6: top-right
            ];
            wPoints.forEach((p, i) => targets.push({ ...p, active: true, order: i + 1 }));
        } else if (currentMode === 'ordered_random') { // random positions, numbered 1-6, click in order
            for (let i = 0; i < 6; i++) {
                let attempts = 0;
                let valid = false;
                let newTarget;
                while (!valid && attempts < 100) {
                    newTarget = {
                        x: Math.random() * (width - 2 * pad) + pad,
                        y: Math.random() * (height - 2 * pad) + pad,
                        active: true,
                        order: i + 1
                    };
                    if (!checkOverlap(newTarget, targets)) valid = true;
                    attempts++;
                }
                if (valid) {
                    targets.push(newTarget);
                } else {
                    targets.push({ x: width / 2 + (i * 10), y: height / 2 + (i * 10), active: true, order: i + 1 });
                }
            }
        }

        return targets;
    };

    // Called when the STANDBY countdown finishes — actually starts the round
    const beginGame = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const state = stateRef.current;
        state.targets = generateTargets(canvas.width, canvas.height, mode);
        state.running = true;
        state.startTime = performance.now();
        state.endTime = 0;
        state.mode = mode;

        setIsCountingDown(false);
        setGameState('playing');
        beep(1000, 0.1);
    }, [mode, beep]);

    // Called by the Start button — hides modal and starts STANDBY countdown
    const startGame = useCallback(() => {
        stateRef.current.running = false;
        setGameState('menu');
        setShowStartModal(false);
        setIsCountingDown(true);
    }, []);

    const finishGame = useCallback((elapsed) => {
        const state = stateRef.current;
        state.running = false;
        state.endTime = elapsed;

        setLastTime(elapsed / 1000);
        saveScore(elapsed / 1000, stateRef.current.mode);
        setGameState('finished');
    }, []);

    // Game Loop
    const loop = useCallback((now, dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const state = stateRef.current;
        const w = canvas.width;
        const h = canvas.height;

        ctx.fillStyle = GAME_THEME.BACKGROUND_COLOR;
        ctx.fillRect(0, 0, w, h);

        if (state.running) {
            const elapsed = now - state.startTime;
            const isOrdered = state.mode === 'w_pattern' || state.mode === 'ordered_random';
            const activeTargets = state.targets.filter(t => t.active);
            const nextOrder = isOrdered && activeTargets.length > 0
                ? Math.min(...activeTargets.map(t => t.order))
                : null;

            // Draw Targets
            const targetsToDraw = state.mode === 'alternate'
                ? activeTargets.slice(0, 1)
                : activeTargets;

            targetsToDraw.forEach(pos => {
                const isNext = isOrdered && pos.order === nextOrder;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, TARGET_RADIUS, 0, Math.PI * 2);
                ctx.fillStyle = (isOrdered && !isNext) ? 'rgba(40, 65, 90, 0.85)' : GAME_THEME.TARGET_FILL;
                ctx.fill();
                ctx.strokeStyle = isNext ? '#ffffff' : (isOrdered ? '#3a5a75' : GAME_THEME.TARGET_STROKE);
                ctx.lineWidth = isNext ? 3 : GAME_THEME.TARGET_STROKE_WIDTH;
                ctx.stroke();

                // Draw order number for ordered modes
                if (isOrdered) {
                    ctx.fillStyle = isNext ? '#ffffff' : '#6a9abc';
                    ctx.font = `bold ${Math.round(TARGET_RADIUS * 0.75)}px Inter`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(pos.order, pos.x, pos.y);
                    ctx.textBaseline = 'alphabetic';
                }
            });

            // Live Timer
            ctx.fillStyle = "white";
            ctx.font = "bold 24px Inter";
            ctx.textAlign = "center";
            ctx.fillText((elapsed / 1000).toFixed(3) + "s", w / 2, 80);

            // Remaining Counter
            const remaining = activeTargets.length;
            ctx.fillStyle = "#aaa";
            ctx.font = "bold 18px Inter";
            ctx.fillText(`Targets Left: ${remaining}`, w / 2, 110);
        }
    }, []);

    useGameLoop(loop, true);

    const handleCanvasClick = (e) => {
        const state = stateRef.current;
        if (!state.running) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        let hit = false;

        // Determine which targets are clickable based on mode
        const isOrdered = state.mode === 'w_pattern' || state.mode === 'ordered_random';
        let checkTargets;
        if (state.mode === 'alternate') {
            checkTargets = state.targets.filter(t => t.active).slice(0, 1);
        } else if (isOrdered) {
            const active = state.targets.filter(t => t.active);
            const nextOrder = active.length > 0 ? Math.min(...active.map(t => t.order)) : null;
            checkTargets = nextOrder !== null ? active.filter(t => t.order === nextOrder) : [];
        } else {
            checkTargets = [...state.targets].reverse().filter(t => t.active);
        }

        for (const t of checkTargets) {
            const dist = Math.hypot(mx - t.x, my - t.y);
            if (dist <= TARGET_RADIUS) {
                t.active = false;
                hit = true;
                break;
            }
        }

        if (hit) {
            ping();
            const activeTargets = state.targets.filter(t => t.active);
            if (activeTargets.length === 0) {
                const elapsed = performance.now() - state.startTime;
                finishGame(elapsed);
            }
        }
    };

    const handleQuit = () => {
        stateRef.current.running = false;
        setIsCountingDown(false);
        setGameState('menu');
        setShowStartModal(true);
    };

    const sidebarContent = (
        <div style={{ color: 'white' }}>
            <h3 style={{ marginBottom: '15px', fontSize: '1.2rem', color: '#f1c40f' }}>Setup</h3>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.9rem', color: '#ccc' }}>Layout Mode</label>
            <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                style={{
                    padding: '10px',
                    borderRadius: '6px',
                    background: '#2c3e50',
                    color: 'white',
                    border: '1px solid #34495e',
                    width: '100%',
                    cursor: 'pointer',
                    outline: 'none',
                    marginBottom: '20px'
                }}
            >
                <option value="random">Random Locations</option>
                <option value="grid">Side by Side (Row)</option>
                <option value="column">Top to Bottom (Column)</option>
                <option value="alternate">Left / Right Alternate</option>
                <option value="w_pattern">W Pattern (Ordered)</option>
                <option value="ordered_random">Random (Ordered)</option>
            </select>

            <button
                onClick={startGame}
                style={{
                    padding: '12px',
                    borderRadius: '6px',
                    border: 'none',
                    background: '#3498db',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    width: '100%',
                    textAlign: 'center',
                    marginBottom: '20px'
                }}
            >
                {gameState === 'finished' ? 'PLAY AGAIN' : 'START GAME'}
            </button>

            <Scoreboard
                stats={{}}
                highScores={highScores[mode] || []}
                onReset={clearScores}
                title={{
                    random: 'Best Times: Random',
                    grid: 'Best Times: Side by Side',
                    column: 'Best Times: Column',
                    alternate: 'Best Times: L/R Alternate',
                    w_pattern: 'Best Times: W Pattern',
                    ordered_random: 'Best Times: Random (Ordered)',
                }[mode] || 'Best Times'}
            />
        </div>
    );

    return (
        <GameLayout
            canvasRef={canvasRef}
            onCanvasClick={handleCanvasClick}
            gameRunning={gameState === 'playing' || isCountingDown}
            onQuit={handleQuit}
            sidebarContent={sidebarContent}
        >
            <div className={styles.container}>

                {/* Countdown STANDBY overlay */}
                {isCountingDown && (
                    <Countdown
                        prepMode={false}
                        onFinish={beginGame}
                    />
                )}

                {/* Initial start modal */}
                {showStartModal && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10,
                        pointerEvents: 'auto'
                    }}>
                        <div style={{
                            background: '#222', padding: '50px', borderRadius: '16px', textAlign: 'center',
                            border: '1px solid #444', color: '#fff', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', minWidth: '350px'
                        }}>
                            <h1 style={{
                                marginTop: 0, fontSize: '2.5em', marginBottom: '10px',
                                background: 'linear-gradient(135deg, #3498db, #2ecc71)',
                                WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                fontWeight: 800
                            }}>
                                Click 6
                            </h1>
                            <p style={{ color: '#ccc', marginBottom: '30px', fontSize: '1.1em', lineHeight: 1.6 }}>
                                Click all 6 targets as fast as possible.<br />
                                <strong>Random:</strong> Scattered anywhere.<br />
                                <strong>Side by Side:</strong> 1 row of 6.<br />
                                <strong>L/R Alternate:</strong> One at a time, left then right.
                            </p>
                            <button
                                onClick={startGame}
                                style={{
                                    padding: '16px 40px', fontSize: '20px', fontWeight: 'bold',
                                    background: GAME_THEME.BUTTON_BG, color: 'white',
                                    border: '1px solid #444', borderRadius: '8px', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                START GAME
                            </button>
                        </div>
                    </div>
                )}

                {/* Finished overlay */}
                {gameState === 'finished' && !showStartModal && (
                    <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        background: 'rgba(0,0,0,0.8)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20,
                        pointerEvents: 'auto'
                    }}>
                        <div style={{
                            background: '#222', padding: '40px', borderRadius: '10px', textAlign: 'center',
                            border: '1px solid #444', color: '#fff'
                        }}>
                            <h2 style={{ color: '#2ecc71', marginTop: 0 }}>COMPLETED!</h2>
                            <div style={{ fontSize: '3rem', fontWeight: 'bold', margin: '20px 0', color: '#f1c40f' }}>
                                {lastTime.toFixed(3)}s
                            </div>
                            <button
                                onClick={startGame}
                                style={{
                                    marginTop: '10px', padding: '12px 30px', fontSize: '18px', fontWeight: 'bold',
                                    background: GAME_THEME.BUTTON_BG, color: 'white',
                                    border: '1px solid #444', borderRadius: '6px', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Play Again
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </GameLayout>
    );
};

export default Click6;
